<template>
	<v-card class="grid-flex-container my-0" elevation="0">
		<v-container fluid class="py-6">
			<v-row>
				<div style="flex-grow: 1">
					<v-text-field
						v-if="canView"
						v-model="search"
						class="py-0 my-0 mb-2 mr-2"
						append-icon="mdi-magnify"
						label="Search"
						single-line
						hide-details
					/>
				</div>
				<v-btn v-if="canAdd" color="primary" @click="showModal()">
					<v-icon> mdi-plus</v-icon>
					&nbsp;&nbsp;Add
				</v-btn>
			</v-row>
		</v-container>
		<v-data-table
			v-if="canView"
			hide-default-footer
			multi-sort
			dense
			fixed-header
			:search="search"
			:items="filters"
			:headers="headers"
			:disable-pagination="true"
			item-value="GridViewId"
			item-key="GridViewId"
			show-expand
			:expanded.sync="expanded"
			@update:expanded="onExpandedUpdate"
		>
			<template v-slot:[`item.actions`]="{ item }">
				<div class="d-flex">
					<v-spacer />
					<v-btn
						v-if="canEdit"
						class="action-btn"
						fab
						small
						icon
						elevation="1"
						color="success"
						@click.stop="editFilter(item)"
					>
						<v-icon small> mdi-pencil</v-icon>
					</v-btn>
					<v-btn
						v-if="canDelete"
						class="action-btn"
						fab
						small
						icon
						elevation="1"
						color="error"
						@click.stop="deleteFilter(item)"
					>
						<v-icon small> mdi-delete</v-icon>
					</v-btn>
				</div>
			</template>
			<template v-slot:expanded-item="{ item }">
				<td :colspan="headers.length + 1" class="ma-0 pa-0">
					<div v-if="!item.resolvedFilters" class="text-center pa-4">
						<v-progress-circular indeterminate size="24" />
						Loading filters...
					</div>
					<v-data-table
						v-else-if="item.Filters && item.Filters.length > 0"
						dense
						fixed-header
						elevation="0"
						:headers="expandedHeaders"
						:items="item.resolvedFilters"
						:hide-default-footer="true"
					>
					</v-data-table>
					<div v-else class="text-center pa-4">
						No filters available.
					</div>
				</td>
			</template>
		</v-data-table>
		<inventory-form-filters-modal
			v-if="showDialog && selected"
			:showDialog="showDialog"
			:filters="filters"
			:selected="selected"
			@cancel="hideModal"
			@submitted="onFilterModalSubmitted"
		/>
	</v-card>
</template>
<script>
import InventoryFormFiltersModal from '../formFilters/InventoryFormFiltersModal.vue';
import { InventoryFormFiltersActions } from '../../../../store/actions';
import { InventoryFormFiltersGetters } from '../../../../store/getters';
import { Permissions } from '../../../../enums/permissionsEnum';
import permissionsMixin from '../../../../mixins/permissionsMixin';
import { advancedFilterOperators } from '../../../../enums/dropdownHelpers/advancedFilterOperators';
import { mapGetters } from 'vuex';
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
export default {
	name: 'InventoryFormFilters',
	components: { InventoryFormFiltersModal },
	mixins: [permissionsMixin],
	data() {
		return {
			showDialog: false,
			selected: null,
			search: '',
			expanded: [],
			headers: [
				{
					text: 'Entity Name',
					value: 'Entity',
				},
				{
					text: 'Count',
					value: 'Count',
				},
				{
					sortable: false,
					text: '',
					value: 'actions',
				},
			],
			expandedHeaders: [
				{ text: 'Field', value: 'FieldId', width: '30%' },
				{ text: 'Operator', value: 'Operator', width: '20%' },
				{ text: 'Value', value: 'Value' },
			],
		};
	},
	computed: {
		...mapGetters({
			filters: [InventoryFormFiltersGetters.FILTERS],
			loading: [InventoryFormFiltersGetters.LOADING],
			fields: [InventoryFormFiltersGetters.FIELDS],
		}),
		canView() {
			return this.hasPermissions([
				Permissions.Tools.FormBuilder.Filters.View,
			]);
		},
		canAdd() {
			return this.hasPermissions([
				Permissions.Tools.FormBuilder.Filters.Add,
			]);
		},
		canEdit() {
			return this.hasPermissions([
				Permissions.Tools.FormBuilder.Filters.Edit,
			]);
		},
		canDelete() {
			return this.hasPermissions([
				Permissions.Tools.FormBuilder.Filters.Delete,
			]);
		},
	},
	mounted() {
		this.$store.dispatch(
			InventoryFormFiltersActions.LOAD_FILTERS,
			this.$route.params.id
		);
	},
	destroyed() {
		this.$store.dispatch(InventoryFormFiltersActions.CLEAR);
	},
	methods: {
		deleteFilter(item) {
			ConfirmDialog({
				message: 'Are you sure you want to delete this item?',
			}).then((success) => {
				if (!success) return;
				this.$store.dispatch(
					InventoryFormFiltersActions.REMOVE_FILTERS,
					{
						inventoryFormId: item.InventoryFormId,
						gridViewId: item.GridViewId,
					}
				);
			});
		},
		editFilter(item) {
			item.Filters.forEach((x) => (x.IsDeleted = null));
			this.selected = JSON.parse(JSON.stringify(item));
			this.showDialog = true;
		},
		showModal() {
			this.selected = Object.assign(
				{},
				{
					EntityId: null,
					Filters: [
						{
							FieldId: null,
							Operator: null,
							Value: null,
							IsDeleted: null,
						},
					],
					InventoryFormId: this.$route.params.id,
				}
			);
			this.showDialog = true;
		},
		hideModal() {
			this.selected = null;
			this.showDialog = false;
		},
		onFilterModalSubmitted(selected) {
			if (selected.GridViewId) {
				let item = this.filters.find(
					(f) => f.GridViewId === selected.GridViewId
				);
				if (item && item.resolvedFilters) {
					this.getFiltersData(item);
				}
			}
		},
		onExpandedUpdate(expandedItems) {
			expandedItems.forEach((item) => {
				if (!item.resolvedFilters) {
					this.getFiltersData(item);
				}
			});
		},
		getFiltersData(item) {
			this.$store
				.dispatch(
					InventoryFormFiltersActions.LOAD_FIELDS,
					item.EntityId
				)
				.then(() => {
					const resolvedFilters = item.Filters.map((filter) => {
						const matchedOperator = this.getOperator(
							filter.Operator
						);
						const matchedField = this.getField(filter.FieldId);
						return {
							FieldId: matchedField
								? matchedField
								: filter.FieldId,
							Operator: matchedOperator
								? matchedOperator
								: filter.Operator,
							Value: filter.Value,
						};
					});

					this.$set(item, 'resolvedFilters', resolvedFilters);
				})
				.catch((error) => {
					console.error('Error loading fields:', error);
				});
		},
		getOperator(item) {
			if (!item) return null;
			let operator = advancedFilterOperators.find((x) => x.value == item);
			if (!operator) return null;
			return operator.label;
		},
		getField(item) {
			if (!item) return null;
			let field = this.fields.find((x) => x.Id == item);
			if (!field) return null;
			return field.Label;
		},
	},
};
</script>
